import { AIInputStateControl } from '@modules/ai-conversation/components/AIInput';
import { EditorContentView } from '@modules/editor/EditorContentView';
import { Stack, Typography } from '@mui/material';
import type { Editor } from '@tiptap/react';
import { AiChatMessage } from 'src/@types/ai';
import { CreatingFilesLoader } from 'src/sections/@dashboard/ai/Chat/AIConversation/AIMessages/AIMessageContent/AISearchThinkResponse/CreatingFilesLoader';
import { GeneratedImage } from 'src/sections/@dashboard/ai/Chat/AIConversation/AIMessages/AIMessageContent/AISearchThinkResponse/GeneratedImage';
import { AISearchSources } from '../AISearchSources';
import { BotMessageLoading } from '../BotMessageLoading';
import { ThinkingHeader } from './ThinkingHeader';
import { ChatResponseStyled, StyledMarkupEditor } from './styles';
import { useAISearchThinkResponse } from './useAISearchThinkResponse';

type Props = {
  message: AiChatMessage;
  editor: Editor;
  store: AIInputStateControl;
  status?: string;
  isLastMessage?: boolean;
};

export const AISearchThinkResponse = ({
  message,
  editor,
  store,
  status,
  isLastMessage,
}: Props) => {
  const { aiResponseContent } = useAISearchThinkResponse({
    message,
    store,
  });

  return (
    <ChatResponseStyled>
      {/* Thinking Process */}
      {aiResponseContent.think && !message.content && (
        <ThinkingHeader message={message} />
      )}

      {/* Final Report */}
      {aiResponseContent.parsedContent && (
        <Stack mb={2} overflow="hidden">
          <StyledMarkupEditor>
            <EditorContentView
              editor={editor}
              content={aiResponseContent.parsedContent}
            />
          </StyledMarkupEditor>
        </Stack>
      )}

      {/* Image */}
      {isLastMessage &&
        status !== 'ready' &&
        aiResponseContent.generateImage &&
        !!aiResponseContent.content &&
        !aiResponseContent.generatedFiles?.length && <CreatingFilesLoader />}

      {!!aiResponseContent.generatedFiles.length && (
        <Stack mb={2} overflow="hidden">
          {aiResponseContent.generatedFiles.map((file, index) => {
            if (file.type !== 'image') return null;
            return <GeneratedImage key={index} file={file} />;
          })}
        </Stack>
      )}

      {/* Loading */}
      {aiResponseContent.status === 'thinking' &&
        !aiResponseContent.parsedContent && <BotMessageLoading />}

      {!!aiResponseContent.searchSources?.length && (
        <AISearchSources searchSources={aiResponseContent.searchSources} />
      )}

      {aiResponseContent?.error && (
        <Stack alignItems="center" textAlign="center" mb={4}>
          <Typography variant="subtitle2" color="error">
            {aiResponseContent?.error}
          </Typography>
        </Stack>
      )}
    </ChatResponseStyled>
  );
};
